import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../lib/supabase';
import CreateFirstOrgModal from '../components/CreateFirstOrgModal';
import { useToast } from './ToastContext';

const OrganizationContext = createContext();

export function OrganizationProvider({ children }) {
  const [organizations, setOrganizations] = useState([]);
  const [currentOrganization, setCurrentOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [showCreateFirstOrgModal, setShowCreateFirstOrgModal] = useState(false);
  const { user } = useAuth();

  const loadPendingInvites = async () => {
    if (!user) return [];
    
    try {
      const { data: invites, error } = await supabase
        .from('organization_invites')
        .select(`
          *,
          organizations (
            id,
            name
          )
        `)
        .eq('email', user.email)
        .is('deleted_at', null);  // Only check for non-deleted invites

      if (error) throw error;
      
      const validInvites = invites || [];
      setPendingInvites(validInvites);
      return validInvites;
    } catch (error) {
      console.error('Error loading pending invites:', error);
      return [];
    }
  };

  const loadOrganizations = async () => {
    try {
      if (!user) {
        setOrganizations([]);
        setCurrentOrganization(null);
        setLoading(false);
        setShowCreateFirstOrgModal(false);
        setPendingInvites([]);
        return;
      }

      setLoading(true);
      
      // First load pending invites
      const invites = await loadPendingInvites();
      
      // Then load organizations
      const { data: memberships, error: membershipError } = await supabase
        .from('organization_members')
        .select(`
          organization_id,
          role,
          organizations (*)
        `)
        .eq('user_id', user.id);

      if (membershipError) throw membershipError;

      if (memberships && memberships.length > 0) {
        const orgs = memberships.map(membership => ({
          ...membership.organizations,
          role: membership.role
        }));
        
        setOrganizations(orgs);
        setShowCreateFirstOrgModal(false);

        const { data: profile } = await supabase
          .from('user_profiles')
          .select('default_organization_id')
          .eq('id', user.id)
          .single();

        const defaultOrg = orgs.find(org => org.id === profile?.default_organization_id) || orgs[0];
        setCurrentOrganization(defaultOrg);
      } else {
        setOrganizations([]);
        setCurrentOrganization(null);
        
        // Only show create first org modal if there are no pending invites
        console.log('Pending invites:', invites.length);
        setShowCreateFirstOrgModal(invites.length === 0);
      }
    } catch (error) {
      console.error('Error loading organizations:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load organizations when user changes
  useEffect(() => {
    if (user) {
      loadOrganizations();
    }
  }, [user]);

  const updateCurrentOrganization = async (organization) => {
    try {
      setCurrentOrganization(organization);
      
      // Update user's default organization
      if (user && organization) {
        await supabase
          .from('user_profiles')
          .update({ default_organization_id: organization.id })
          .eq('id', user.id);
      }
    } catch (error) {
      console.error('Error updating current organization:', error);
    }
  };

  const value = {
    organizations,
    currentOrganization,
    setCurrentOrganization: updateCurrentOrganization,
    loading,
    refreshOrganizations: loadOrganizations,
    showCreateFirstOrgModal,
    pendingInvites,
    refreshInvites: loadPendingInvites
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
}

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
}; 